import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import LandingNavBar from '../../components/LandingNavBar';
import LandingFooter from '../../components/LandingFooter';
import {
  ArrowRight, ArrowUpRight, CheckCircle2, Send, Users, Sparkles, ShieldCheck,
  Building2, Bell, Smartphone, Briefcase, Coffee, Store, Hotel, ChevronDown,
  Phone, Calendar, UserCircle2, Link2, Zap, MousePointerClick,
  ScrollText, Quote, Star, Plus, Hash
} from 'lucide-react';

const TELEGRAM_BOT_IMG = 'https://customer-assets.emergentagent.com/job_d5d2a817-6920-419a-9713-eaf4c30fc60a/artifacts/mbmk2yay_ChatGPT%20Image%20May%2015%2C%202026%2C%2010_16_32%20AM.png';
const HR_OG_IMAGE = 'https://customer-assets.emergentagent.com/job_d5d2a817-6920-419a-9713-eaf4c30fc60a/artifacts/pz6gsbxo_ChatGPT%20Image%20May%2020%2C%202026%2C%2012_39_33%20PM.png';

/* ════════════════════════════ Hero ════════════════════════════ */
function Hero() {
  return (
    <section className="relative pt-24 pb-16 lg:pt-28 lg:pb-24 bg-[#FFFBF2] overflow-hidden border-b-2 border-[#0B1120]">
      {/* paper-texture grid */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{ backgroundImage: 'linear-gradient(#0B1120 1px, transparent 1px), linear-gradient(90deg, #0B1120 1px, transparent 1px)', backgroundSize: '32px 32px' }} />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        {/* tiny editorial heading */}
        <div className="flex items-center gap-3 mb-8">
          <Hash className="w-4 h-4 text-emerald-700" />
          <span className="text-[11px] font-mono uppercase tracking-[0.25em] text-emerald-700">Issue 01 · Free HR System for Malaysia</span>
          <div className="flex-1 h-px bg-[#0B1120]/10" />
          <span className="text-[11px] font-mono text-[#0B1120]/40">est. 2026</span>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-end">
          {/* Headline column */}
          <div className="lg:col-span-7">
            <h1 className="text-[44px] sm:text-[64px] lg:text-[88px] leading-[0.95] font-black text-[#0B1120] mb-7 tracking-[-0.03em]" style={{ fontFamily: "'Plus Jakarta Sans','Manrope',sans-serif" }} data-testid="hr-hero-h1">
              Hire your next<br/>
              <span className="relative inline-block">
                <span className="relative z-10">staff member</span>
                <span className="absolute left-0 right-0 bottom-1 h-4 bg-amber-300/70 -z-0 rounded-sm" />
              </span>
              {' '}without<br/>
              paying a sen.
            </h1>

            <p className="text-lg lg:text-xl text-[#0B1120]/70 leading-relaxed max-w-xl mb-8" style={{ fontFamily: "'Lora','Georgia',serif" }}>
              StayMii HR&trade; is a <em>completely free</em> applicant tracking system for
              Malaysian hotels, homestays, cafés, retail shops &amp; SMEs. Get a public
              interview form link in 60 seconds — and a Telegram ping the moment
              someone applies.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-10">
              <Link to="/auth/signup?category=hr" data-testid="cta-create-free-hr-form" className="group inline-flex items-center justify-center gap-2 px-7 py-4 bg-[#0B1120] hover:bg-[#1a2138] text-white rounded-none font-bold text-sm transition-all border-2 border-[#0B1120]" style={{ boxShadow: '5px 5px 0 #10B981' }}>
                Create Free HR Form <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link to="/hr/apply" data-testid="cta-view-sample-form" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-amber-300 hover:bg-amber-200 text-[#0B1120] border-2 border-[#0B1120] rounded-none font-bold text-sm transition-all">
                View Live Sample <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/auth/login" data-testid="cta-login-staymii-hr" className="inline-flex items-center justify-center gap-2 px-7 py-4 bg-white hover:bg-amber-50 text-[#0B1120] border-2 border-[#0B1120] rounded-none font-bold text-sm transition-all">
                Login
              </Link>
            </div>

            {/* Tag strip */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-mono text-[#0B1120]/55">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> FREE FOREVER</span>
              <span className="text-[#0B1120]/20">/</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> NO CARD</span>
              <span className="text-[#0B1120]/20">/</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> 60-SEC SETUP</span>
              <span className="text-[#0B1120]/20">/</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> PDPA-READY</span>
            </div>
          </div>

          {/* Form mock column */}
          <div className="lg:col-span-5">
            <div className="relative">
              {/* sticker */}
              <div className="absolute -top-6 -left-4 z-20 -rotate-6 bg-amber-300 text-[#0B1120] px-4 py-1.5 font-black text-xs tracking-wider border-2 border-[#0B1120]" style={{ boxShadow: '3px 3px 0 #0B1120' }}>
                100% FREE — NO TRICK
              </div>

              <div className="bg-white border-2 border-[#0B1120] rounded-none overflow-hidden" style={{ boxShadow: '12px 12px 0 #0B1120' }}>
                <div className="bg-[#0B1120] text-white px-4 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2 font-mono text-[10px]">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-300" />
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
                  </div>
                  <Link to="/hr/apply" className="text-[10px] font-mono text-white/70 hover:text-emerald-300 underline underline-offset-2" data-testid="hero-mock-sample-url">
                    staymii.com/hr/apply
                  </Link>
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 mb-5 pb-4 border-b border-[#0B1120]/10">
                    <div className="w-11 h-11 bg-emerald-500 flex items-center justify-center border-2 border-[#0B1120]">
                      <Briefcase className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="text-[#0B1120] font-bold text-sm">Merlton Hospitality</div>
                      <div className="text-[11px] text-[#0B1120]/50 font-mono">// Job Application Form</div>
                    </div>
                  </div>
                  {[
                    { label: 'FULL NAME', value: 'Aisyah Rahman' },
                    { label: 'IC NUMBER', value: '990512-08-1234' },
                    { label: 'POSITION', value: 'Front Desk Officer' },
                    { label: 'PHONE', value: '012-3456789' },
                  ].map((f) => (
                    <div key={f.label} className="mb-3">
                      <div className="text-[9px] font-mono text-[#0B1120]/40 tracking-wider mb-1">{f.label}</div>
                      <div className="px-3 py-2 bg-amber-50 border border-[#0B1120]/10 text-sm text-[#0B1120] font-medium">{f.value}</div>
                    </div>
                  ))}
                  <button className="w-full mt-2 py-3 bg-emerald-500 hover:bg-emerald-400 text-[#0B1120] text-xs font-black tracking-wider border-2 border-[#0B1120] transition-colors">
                    SUBMIT APPLICATION →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════ Ticker / Trust strip ═══════════════════════ */
function Ticker() {
  const items = ['HOTELS', 'HOMESTAYS', 'CAFÉS', 'RETAIL', 'SMEs', 'BOUTIQUES', 'F&B', 'AIRBNB HOSTS', 'OFFICES', 'CLINICS'];
  return (
    <div className="bg-[#0B1120] text-white py-4 border-y-2 border-[#0B1120] overflow-hidden">
      <div className="flex gap-10 animate-marquee whitespace-nowrap">
        {[...items, ...items, ...items].map((t, i) => (
          <span key={i} className="text-sm font-bold tracking-[0.3em] inline-flex items-center gap-10">
            {t}
            <Star className="w-3 h-3 text-amber-300 fill-amber-300" />
          </span>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════ How It Works (timeline) ══════════════════════ */
const STEPS = [
  { num: 1, icon: UserCircle2, title: 'Sign up to StayMii (free)', desc: 'One account works across PMS, e-Invoice, and HR. No card required.' },
  { num: 2, icon: Building2, title: 'Register your company', desc: 'Just your business name. We generate a permanent URL slug instantly.' },
  { num: 3, icon: Link2, title: 'Share your form anywhere', desc: 'Drop staymii.com/hr/{your-slug}/apply on WhatsApp, FB, IG bio, JobStreet. See a live demo at staymii.com/hr/apply.' },
  { num: 4, icon: Bell, title: 'Telegram pings on every CV', desc: 'Optional 3-min setup. New applicants land in your Telegram instantly.' },
];

function HowItWorks() {
  return (
    <section className="py-20 lg:py-28 bg-white" data-testid="how-it-works-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-5">
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-emerald-700 mb-3">§ 02 · The Workflow</div>
            <h2 className="text-4xl lg:text-5xl font-black text-[#0B1120] tracking-[-0.02em] leading-[1.05]" style={{ fontFamily: "'Plus Jakarta Sans','Manrope',sans-serif" }}>
              Four steps.<br/>
              <span className="text-emerald-600 italic" style={{ fontFamily: "'Lora',serif" }}>That's it.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-3">
            <p className="text-lg text-[#0B1120]/70 leading-relaxed" style={{ fontFamily: "'Lora','Georgia',serif" }}>
              Most HR software wants a kickoff call and a 14-day implementation plan.
              We want you running before your kopi gets cold. The whole flow is built
              for owners who'd rather be on the floor than behind a screen.
            </p>
          </div>
        </div>

        {/* Timeline */}
        <div className="relative">
          <div className="absolute left-[27px] sm:left-[39px] top-2 bottom-2 w-0.5 bg-[#0B1120]/10 lg:hidden" />
          <div className="hidden lg:block absolute left-0 right-0 top-[39px] h-0.5 bg-[#0B1120]/10" />
          <div className="grid lg:grid-cols-4 gap-8">
            {STEPS.map((s) => (
              <div key={s.num} className="relative flex lg:flex-col gap-5 lg:gap-0">
                <div className="relative shrink-0">
                  <div className="w-14 h-14 sm:w-20 sm:h-20 lg:w-20 lg:h-20 bg-[#0B1120] text-white flex items-center justify-center font-black text-xl lg:text-2xl border-2 border-[#0B1120] relative z-10" style={{ boxShadow: '4px 4px 0 #10B981' }}>
                    {String(s.num).padStart(2, '0')}
                  </div>
                </div>
                <div className="lg:mt-7">
                  <div className="flex items-center gap-2 mb-2">
                    <s.icon className="w-4 h-4 text-emerald-600" />
                    <h3 className="text-base font-bold text-[#0B1120] leading-tight">{s.title}</h3>
                  </div>
                  <p className="text-sm text-[#0B1120]/65 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ════════════════ Try Live Sample ════════════════ */
function TrySampleSection() {
  return (
    <section className="py-14 lg:py-16 bg-amber-50 border-y-2 border-[#0B1120]" data-testid="try-sample-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row items-start lg:items-center gap-6 lg:gap-10">
          <div className="flex-1">
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-emerald-700 mb-2">// Try Before You Sign Up</div>
            <h2 className="text-2xl lg:text-3xl font-black text-[#0B1120] mb-3 tracking-[-0.02em]" style={{ fontFamily: "'Plus Jakarta Sans','Manrope',sans-serif" }}>
              See exactly what your applicants will see.
            </h2>
            <p className="text-[#0B1120]/70 leading-relaxed mb-2" style={{ fontFamily: "'Lora','Georgia',serif" }}>
              We host a live sample of the StayMii HR application form at the URL below.
              Open it on your phone or laptop, scroll through every section, and try the fields.
              It looks identical to the real branded form you'll get after signing up.
            </p>
            <div className="inline-flex items-center gap-2 mt-3 px-4 py-2.5 bg-white border-2 border-[#0B1120] font-mono text-sm">
              <Link2 className="w-4 h-4 text-emerald-600" />
              <a href="/hr/apply" className="text-[#0B1120] hover:text-emerald-700 font-bold underline underline-offset-2" data-testid="sample-url-text">staymii.com/hr/apply</a>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <Link to="/hr/apply" data-testid="cta-try-sample" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-emerald-500 hover:bg-emerald-400 text-[#0B1120] font-bold text-sm border-2 border-[#0B1120]" style={{ boxShadow: '4px 4px 0 #0B1120' }}>
              Open Live Sample <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link to="/auth/signup?category=hr" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white hover:bg-amber-100 text-[#0B1120] font-bold text-sm border-2 border-[#0B1120]">
              Sign Up Free <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═════════════════════ Pull Quote / Editorial Break ═════════════════════ */
function PullQuote() {
  return (
    <section className="py-16 lg:py-24 bg-emerald-600 text-white relative overflow-hidden border-y-2 border-[#0B1120]">
      <Quote className="absolute -top-4 -left-4 w-32 h-32 text-white/10" />
      <Quote className="absolute -bottom-4 -right-4 w-32 h-32 text-white/10 rotate-180" />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
        <p className="text-2xl sm:text-3xl lg:text-4xl font-medium leading-snug tracking-[-0.01em]" style={{ fontFamily: "'Lora','Georgia',serif" }}>
          "We replaced our WhatsApp CV chaos with a StayMii HR form in ten minutes.
          Telegram alerts mean I never miss a candidate, even on a busy turnover day."
        </p>
        <div className="mt-6 inline-flex items-center gap-3 text-sm">
          <div className="w-10 h-10 rounded-full bg-amber-300 text-[#0B1120] flex items-center justify-center font-black border-2 border-[#0B1120]">L</div>
          <div className="text-left">
            <div className="font-bold">Lim, Owner</div>
            <div className="text-white/70 font-mono text-[11px]">Boutique Homestay · Penang</div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════ Telegram Section (with image) ════════════════════ */
function TelegramSection() {
  return (
    <section className="py-20 lg:py-28 bg-[#FFFBF2]" data-testid="telegram-demo-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#2AABEE] mb-3">§ 03 · Notifications</div>
            <h2 className="text-4xl lg:text-5xl font-black text-[#0B1120] mb-5 tracking-[-0.02em] leading-[1.05]" style={{ fontFamily: "'Plus Jakarta Sans','Manrope',sans-serif" }}>
              Every CV. Direct to your{' '}
              <span className="relative inline-block">
                <span className="relative z-10 text-[#2AABEE]">Telegram</span>
                <span className="absolute left-0 right-0 bottom-1 h-3 bg-[#2AABEE]/20 -z-0" />
              </span>.
            </h2>
            <p className="text-lg text-[#0B1120]/70 leading-relaxed mb-7" style={{ fontFamily: "'Lora','Georgia',serif" }}>
              Powered by <strong>@instant_email_bot</strong> — a free Telegram bot
              that bridges email → chat. The moment an applicant submits your form,
              a Telegram message lands in your pocket with their name, position, phone,
              and interview slot (if any).
            </p>

            <ul className="space-y-3 mb-8">
              {[
                ['30-day complimentary trial', 'Bundled free when you sign up to StayMii HR.'],
                ['Self-service setup', 'About 3 minutes. No code, no servers, no API keys.'],
                ['Up to 3 chat bridges', 'Send alerts to multiple managers or shift leaders.'],
                ['Then USD 14.99/year', 'Billed by @instant_email_bot, not by us.'],
              ].map(([t, sub]) => (
                <li key={t} className="flex items-start gap-3">
                  <div className="w-5 h-5 mt-0.5 rounded-full bg-[#2AABEE] flex items-center justify-center shrink-0">
                    <CheckCircle2 className="w-3 h-3 text-white" />
                  </div>
                  <div>
                    <div className="text-sm font-bold text-[#0B1120]">{t}</div>
                    <div className="text-sm text-[#0B1120]/60">{sub}</div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap gap-3">
              <Link to="/auth/signup?category=hr" data-testid="cta-activate-telegram" className="inline-flex items-center gap-2 px-6 py-3.5 bg-[#2AABEE] hover:bg-[#1f97d1] text-white font-bold text-sm border-2 border-[#0B1120]" style={{ boxShadow: '4px 4px 0 #0B1120' }}>
                Activate Telegram Alerts <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="https://t.me/instant_email_bot" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3.5 bg-white text-[#0B1120] font-bold text-sm border-2 border-[#0B1120] hover:bg-amber-50">
                Open @instant_email_bot <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Right: provided image */}
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="relative">
              <div className="absolute -top-3 -right-3 z-10 bg-[#2AABEE] text-white px-3 py-1.5 font-mono font-bold text-[10px] tracking-widest border-2 border-[#0B1120]" style={{ boxShadow: '3px 3px 0 #0B1120' }}>
                @INSTANT_EMAIL_BOT
              </div>
              <div className="bg-white border-2 border-[#0B1120] overflow-hidden" style={{ boxShadow: '12px 12px 0 #2AABEE' }}>
                <img
                  src={TELEGRAM_BOT_IMG}
                  alt="@instant_email_bot — free temp email service inside Telegram, used as the bridge for StayMii HR notifications"
                  className="w-full h-auto block"
                  data-testid="instant-email-bot-image"
                  loading="lazy"
                />
              </div>
              <p className="mt-4 text-xs text-[#0B1120]/50 font-mono italic text-center">
                FIG. 1 — The Telegram bot that bridges your applicant emails to your phone.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════ Applicant Dashboard Preview ════════════════════ */
function DashboardPreview() {
  const rows = [
    { name: 'Aisyah Rahman', ic: '990512-08-XXXX', position: 'Front Desk Officer', date: '20 May 2026', status: 'New', statusColor: 'bg-blue-100 text-blue-700 border-blue-300' },
    { name: 'Daniel Tan', ic: '950201-14-XXXX', position: 'Housekeeping Sup.', date: '19 May 2026', status: 'Interview', statusColor: 'bg-amber-100 text-amber-700 border-amber-300' },
    { name: 'Siti Nurhaliza', ic: '020815-10-XXXX', position: 'F&B Service Crew', date: '19 May 2026', status: 'Shortlisted', statusColor: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
    { name: 'Raj Kumar', ic: '880830-06-XXXX', position: 'Maintenance Tech', date: '18 May 2026', status: 'New', statusColor: 'bg-blue-100 text-blue-700 border-blue-300' },
  ];
  return (
    <section className="py-20 lg:py-28 bg-white" data-testid="dashboard-preview-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-12">
          <div className="lg:col-span-7">
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-emerald-700 mb-3">§ 04 · The Dashboard</div>
            <h2 className="text-4xl lg:text-5xl font-black text-[#0B1120] tracking-[-0.02em] leading-[1.05]" style={{ fontFamily: "'Plus Jakarta Sans','Manrope',sans-serif" }}>
              Every applicant.<br />One clean inbox.
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-3">
            <p className="text-lg text-[#0B1120]/70 leading-relaxed" style={{ fontFamily: "'Lora','Georgia',serif" }}>
              Telegram gives the quick ping. Need the full picture — resume, IC,
              education, work history? Log in to{' '}
              <strong className="text-emerald-700">HR Module → Applicants</strong>.
            </p>
          </div>
        </div>

        <div className="border-2 border-[#0B1120]" style={{ boxShadow: '12px 12px 0 #FCD34D' }}>
          <div className="bg-[#181B30] px-5 py-3 flex items-center justify-between border-b-2 border-[#0B1120]">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-emerald-500 flex items-center justify-center border border-white/20">
                <Users className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="text-white text-sm font-bold">StayMii <span className="text-emerald-400">HR</span></span>
            </div>
            <div className="hidden sm:flex items-center gap-2 font-mono text-[10px] text-emerald-300">
              <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" /> 12 ACTIVE APPLICANTS
            </div>
          </div>
          <div className="flex">
            <aside className="hidden md:flex flex-col w-[170px] bg-[#181B30] border-r border-white/5 p-3">
              <p className="text-[9px] text-white/40 uppercase tracking-widest font-bold px-2 mb-3">Onboarding HR</p>
              <div className="px-2 py-2.5 bg-emerald-600/20 text-white text-[10px] font-bold tracking-wider flex items-center gap-2 border-l-2 border-emerald-500">
                <Users className="w-3.5 h-3.5 text-emerald-400" /> APPLICANTS
              </div>
              <div className="px-2 py-2.5 text-white/50 text-[10px] font-bold tracking-wider flex items-center gap-2 mt-1">
                <Building2 className="w-3.5 h-3.5" /> SETTINGS
              </div>
            </aside>
            <div className="flex-1 bg-white p-5 sm:p-7">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-lg font-black text-[#0B1120]">Applicants</h3>
                  <p className="text-xs text-[#0B1120]/50 font-mono mt-0.5">// 12 TOTAL APPLICATIONS</p>
                </div>
                <button className="text-[11px] font-bold text-white bg-emerald-600 hover:bg-emerald-700 px-3 py-1.5">Company Settings</button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-[12px]">
                  <thead>
                    <tr className="text-left text-[10px] text-[#0B1120]/50 uppercase tracking-wider border-b-2 border-[#0B1120]">
                      <th className="py-2 pr-4 font-bold">Name</th>
                      <th className="py-2 pr-4 font-bold hidden sm:table-cell">IC Number</th>
                      <th className="py-2 pr-4 font-bold">Position</th>
                      <th className="py-2 pr-4 font-bold hidden md:table-cell">Date</th>
                      <th className="py-2 pr-4 font-bold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.name} className="border-b border-[#0B1120]/10 hover:bg-amber-50 transition-colors">
                        <td className="py-3 pr-4 font-bold text-[#0B1120]">{r.name}</td>
                        <td className="py-3 pr-4 text-[#0B1120]/70 hidden sm:table-cell font-mono">{r.ic}</td>
                        <td className="py-3 pr-4 text-[#0B1120]/80">{r.position}</td>
                        <td className="py-3 pr-4 text-[#0B1120]/50 hidden md:table-cell font-mono">{r.date}</td>
                        <td className="py-3 pr-4"><span className={`px-2 py-0.5 text-[10px] font-bold border ${r.statusColor}`}>{r.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════ Who It's For ════════════════════════ */
function WhoItsFor() {
  const segments = [
    { icon: Hotel, label: 'Hotels & Resorts', tag: 'Front desk · housekeeping · F&B' },
    { icon: Building2, label: 'Homestays & Airbnb', tag: 'Cleaners · caretakers' },
    { icon: Coffee, label: 'Cafés & F&B', tag: 'Baristas · servers · kitchen' },
    { icon: Store, label: 'Retail Shops', tag: 'Promoters · sales · cashiers' },
    { icon: Briefcase, label: 'Offices & SMEs', tag: 'Admin · interns · support' },
    { icon: Smartphone, label: 'On-the-go Owners', tag: 'Review CVs from your phone' },
  ];
  return (
    <section className="py-20 lg:py-24 bg-[#0B1120] text-white border-y-2 border-[#0B1120]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="mb-12">
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-emerald-400 mb-3">§ 05 · Who It Serves</div>
          <h2 className="text-4xl lg:text-5xl font-black tracking-[-0.02em] leading-[1.05]" style={{ fontFamily: "'Plus Jakarta Sans','Manrope',sans-serif" }}>
            Built for the<br />
            <span className="text-amber-300">small Malaysian business.</span>
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {segments.map((s, i) => (
            <div key={s.label} className="group p-6 border-2 border-white/10 hover:border-emerald-400 hover:bg-white/5 transition-all">
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-emerald-500/10 border border-emerald-400/30 flex items-center justify-center group-hover:bg-emerald-500/20 transition-colors">
                  <s.icon className="w-5 h-5 text-emerald-400" />
                </div>
                <span className="font-mono text-[10px] text-white/30">{String(i + 1).padStart(2, '0')}</span>
              </div>
              <h3 className="text-lg font-bold mb-1">{s.label}</h3>
              <p className="text-xs text-white/60 font-mono">{s.tag}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════ Pricing (receipt style) ════════════════════════ */
function Pricing() {
  const lines = [
    ['StayMii HR Module', 'FREE'],
    ['Public HR Form Generation', 'FREE'],
    ['Applicant Dashboard', 'FREE'],
    ['Mobile-Friendly Forms', 'FREE'],
    ['Unlimited Applications', 'FREE'],
    ['PDPA-Compliant Storage', 'FREE'],
  ];
  return (
    <section className="py-20 lg:py-28 bg-[#FFFBF2]" data-testid="pricing-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-5">
            <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-emerald-700 mb-3">§ 06 · The Bill</div>
            <h2 className="text-4xl lg:text-5xl font-black text-[#0B1120] mb-5 tracking-[-0.02em] leading-[1.05]" style={{ fontFamily: "'Plus Jakarta Sans','Manrope',sans-serif" }}>
              The shortest pricing page<br />
              <span className="italic text-emerald-600" style={{ fontFamily: "'Lora',serif" }}>you'll ever read.</span>
            </h2>
            <p className="text-lg text-[#0B1120]/70 leading-relaxed mb-6" style={{ fontFamily: "'Lora','Georgia',serif" }}>
              StayMii HR is genuinely free. Forever. The only optional cost is the
              third-party Telegram bridge — and that's billed by
              <strong> @instant_email_bot</strong>, not us.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/auth/signup?category=hr" data-testid="cta-register-company-pricing" className="inline-flex items-center gap-2 px-6 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm border-2 border-[#0B1120]" style={{ boxShadow: '4px 4px 0 #0B1120' }}>
                Register Company <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/auth/login" className="inline-flex items-center gap-2 px-6 py-3.5 bg-white text-[#0B1120] font-bold text-sm border-2 border-[#0B1120] hover:bg-amber-50">
                Login to StayMii HR
              </Link>
            </div>
          </div>

          {/* Receipt */}
          <div className="lg:col-span-7">
            <div className="bg-white border-2 border-[#0B1120] p-7 sm:p-9 font-mono" style={{ boxShadow: '14px 14px 0 #10B981' }}>
              <div className="flex items-start justify-between border-b-2 border-dashed border-[#0B1120]/30 pb-4 mb-5">
                <div>
                  <div className="text-2xl font-black tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans','Manrope',sans-serif" }}>STAYMII HR</div>
                  <div className="text-[10px] text-[#0B1120]/50 mt-1">// INVOICE · KUALA LUMPUR, MY</div>
                </div>
                <div className="text-right text-[10px] text-[#0B1120]/50">
                  No. 0000-001<br />2026-FOREVER
                </div>
              </div>

              {lines.map(([k, v]) => (
                <div key={k} className="flex justify-between items-baseline py-2 border-b border-[#0B1120]/10 text-[13px]">
                  <span className="text-[#0B1120]">{k}</span>
                  <span className="font-bold text-emerald-700 tracking-wider">{v}</span>
                </div>
              ))}

              <div className="mt-3 pt-3 border-t-2 border-[#0B1120]/30">
                <div className="text-[10px] text-[#0B1120]/50 mb-2">OPTIONAL · BILLED BY @instant_email_bot</div>
                <div className="flex justify-between items-baseline py-1.5 text-[13px]">
                  <span className="text-[#0B1120]">Telegram Setup</span>
                  <span className="font-bold text-[#2AABEE]">SELF-SERVE</span>
                </div>
                <div className="flex justify-between items-baseline py-1.5 text-[13px]">
                  <span className="text-[#0B1120]">Trial (30 days)</span>
                  <span className="font-bold text-[#2AABEE]">FREE</span>
                </div>
                <div className="flex justify-between items-baseline py-1.5 text-[13px]">
                  <span className="text-[#0B1120]">Renewal</span>
                  <span className="font-bold text-[#0B1120]">USD 14.99 / yr</span>
                </div>
              </div>

              <div className="mt-5 pt-5 border-t-2 border-[#0B1120] flex justify-between items-baseline">
                <span className="text-sm font-bold text-[#0B1120]">YOU PAY STAYMII</span>
                <span className="text-3xl font-black text-emerald-600 tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans','Manrope',sans-serif" }}>RM 0.00</span>
              </div>

              <div className="mt-5 text-center text-[10px] text-[#0B1120]/40 tracking-widest">
                ★ ★ ★ THANK YOU FOR HIRING WITH STAYMII ★ ★ ★
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════════ FAQ ════════════════════════════ */
const FAQS = [
  { q: 'Can I see what the form actually looks like before signing up?', a: 'Yes — open staymii.com/hr/apply for a live demo. It shows exactly what your applicants will see (job position, IC, contact, education, work history, resume upload, PDPA consent). Submission is disabled in demo mode — only your real, branded form at staymii.com/hr/your-company/apply accepts applicants.' },
  { q: 'Is StayMii HR really free?', a: 'Yes — the HR Module, applicant tracking, dashboard, and public form generation are 100% free with no setup fees, no card required, and no usage limits. We earn revenue from other StayMii modules (PMS, e-Invoice) and offer HR free as a contribution to Malaysian SMEs.' },
  { q: 'What is the public HR form link?', a: 'When you register your company, StayMii instantly generates a permanent URL — e.g. staymii.com/hr/your-company/apply. Share it on JobStreet, WhatsApp Status, Facebook, Instagram bio, printed posters — anywhere. Applicants fill in, you get notified.' },
  { q: 'How do Telegram notifications work?', a: 'Powered by @instant_email_bot — a free Telegram bot that bridges email → Telegram. Sign up via @instant_email_bot to get a unique bridge address, paste it into HR Settings → Email Setup, and you\'re live in about 3 minutes.' },
  { q: 'What happens after the 30-day Telegram trial?', a: '@instant_email_bot offers a complimentary 30-day trial bundled with StayMii HR. After the trial, their service costs USD 14.99/year — paid directly to them. StayMii itself remains free regardless.' },
  { q: 'What data do applicants submit?', a: 'Full name, IC/passport, contact number, position applied, work experience, education, and resume upload. The Telegram alert previews the essentials; the full record is available by logging in to HR Module → Applicants.' },
  { q: 'Can I customise the form fields?', a: 'The form already covers all standard Malaysian hiring fields. Custom-field support is on the roadmap — drop us a line via Contact if it\'s a blocker.' },
  { q: 'Is StayMii HR PDPA-compliant?', a: 'Yes. All applicant data is stored on encrypted Malaysian-region infrastructure with role-based access. See our Privacy Policy and Trust Center for the full breakdown.' },
  { q: 'Can I use StayMii HR without using StayMii PMS or e-Invoice?', a: 'Absolutely — HR is a standalone module. Sign up, register your company, and you\'re running. No PMS or e-Invoice subscription needed.' },
];

function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section className="py-20 lg:py-28 bg-white" data-testid="faq-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="text-center mb-12">
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-emerald-700 mb-3">§ 07 · Q&A</div>
          <h2 className="text-4xl lg:text-5xl font-black text-[#0B1120] tracking-[-0.02em] leading-[1.05]" style={{ fontFamily: "'Plus Jakarta Sans','Manrope',sans-serif" }}>
            Things owners ask us<br />
            <span className="italic text-emerald-600" style={{ fontFamily: "'Lora',serif" }}>before they sign up.</span>
          </h2>
        </div>
        <div className="border-t-2 border-[#0B1120]">
          {FAQS.map((item, i) => (
            <div key={i} className="border-b-2 border-[#0B1120]">
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-start justify-between gap-4 px-2 py-5 text-left hover:bg-amber-50 transition-colors group"
                data-testid={`faq-toggle-${i}`}
              >
                <div className="flex items-start gap-4">
                  <span className="font-mono text-xs text-emerald-700 font-bold mt-1.5 shrink-0">Q{String(i + 1).padStart(2, '0')}.</span>
                  <span className="text-base sm:text-lg font-bold text-[#0B1120] leading-snug">{item.q}</span>
                </div>
                <Plus className={`w-6 h-6 text-[#0B1120] shrink-0 transition-transform ${open === i ? 'rotate-45' : ''}`} />
              </button>
              {open === i && (
                <div className="pb-6 pl-12 pr-4 text-base text-[#0B1120]/75 leading-relaxed" style={{ fontFamily: "'Lora','Georgia',serif" }}>
                  {item.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ════════════════════════ Final CTA ════════════════════════ */
function FinalCTA() {
  return (
    <section className="py-24 lg:py-32 bg-[#0B1120] text-white relative overflow-hidden border-t-2 border-[#0B1120]">
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{ backgroundImage: 'linear-gradient(#fff 1px, transparent 1px), linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
        <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-emerald-400 mb-5">END · § 08</div>
        <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black mb-7 tracking-[-0.03em] leading-[0.95]" style={{ fontFamily: "'Plus Jakarta Sans','Manrope',sans-serif" }}>
          Start hiring.<br />
          <span className="text-emerald-400">Pay nothing.</span>
        </h2>
        <p className="text-lg lg:text-xl text-white/65 mb-10 max-w-2xl mx-auto" style={{ fontFamily: "'Lora','Georgia',serif" }}>
          Your public form. Your Telegram alerts. Your applicant inbox. All free, forever.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/auth/signup?category=hr" data-testid="cta-final-create-form" className="group inline-flex items-center justify-center gap-2 px-8 py-4 bg-emerald-500 hover:bg-emerald-400 text-[#0B1120] font-black text-sm border-2 border-emerald-500 transition-all" style={{ boxShadow: '5px 5px 0 #FCD34D' }}>
            <MousePointerClick className="w-4 h-4" /> Create Free HR Form
          </Link>
          <Link to="/auth/login" data-testid="cta-final-login" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-transparent text-white border-2 border-white/30 hover:bg-white/5 font-bold text-sm">
            Login to StayMii HR
          </Link>
        </div>
        <p className="text-[10px] font-mono text-white/40 mt-8 tracking-widest flex items-center justify-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5" /> PDPA-COMPLIANT · MY-HOSTED · USED BY HOTELS · CAFÉS · RETAIL · SMEs
        </p>
      </div>
    </section>
  );
}

/* ════════════════════════════ Page ════════════════════════════ */
export default function StayMiiHR() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'StayMii HR',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      offers: { '@type': 'Offer', price: '0', priceCurrency: 'MYR' },
      description: 'Free HR & applicant tracking system for Malaysian hotels, homestays, cafés, retail shops, and SMEs. Generate a public interview form, share publicly, receive instant Telegram alerts via @instant_email_bot.',
    };
    const faqLd = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const tag1 = document.createElement('script');
    tag1.type = 'application/ld+json';
    tag1.id = 'staymii-hr-jsonld';
    tag1.text = JSON.stringify(jsonLd);
    document.head.appendChild(tag1);
    const tag2 = document.createElement('script');
    tag2.type = 'application/ld+json';
    tag2.id = 'staymii-hr-faq-jsonld';
    tag2.text = JSON.stringify(faqLd);
    document.head.appendChild(tag2);
    return () => { tag1.remove(); tag2.remove(); };
  }, []);

  return (
    <>
      <SEO
        title="Free HR Application Form & Job Application Form Template — StayMii HR Malaysia"
        description="Free HR application form and applicant tracking system for Malaysian hotels, homestays, cafés, retail, and SMEs. Generate a public job application form template at staymii.com/hr/your-company/apply, receive instant Telegram alerts via @instant_email_bot, and manage every applicant from one free dashboard. No setup fee, no card."
        keywords="free hr system malaysia, hr application form, free job application form, free job application form template, job application form template, online job application form, applicant tracking system malaysia, hr interview form, recruitment form malaysia, telegram hr notification, hotel hr management, hospitality hr malaysia, free recruitment software malaysia, hr software sme malaysia, employment application form malaysia, job application form pdf alternative, instant email bot, online interview form system, free employee application form"
        canonicalUrl="/staymii-hr"
        ogImage={HR_OG_IMAGE}
      />
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 40s linear infinite;
        }
      `}</style>
      <div className="min-h-screen bg-white" data-testid="staymii-hr-landing">
        <LandingNavBar />
        <Hero />
        <Ticker />
        <HowItWorks />
        <TrySampleSection />
        <PullQuote />
        <TelegramSection />
        <DashboardPreview />
        <WhoItsFor />
        <Pricing />
        <FAQ />
        <FinalCTA />
        <LandingFooter />
      </div>
    </>
  );
}
